import React, { useEffect, useState } from 'react';
import {
    Modal, Form, Input, Select, InputNumber, Row, Col,
    Switch, message, DatePicker
} from 'antd';
import dayjs from 'dayjs';
import * as adminactions from 'store/actions/AuthActions';

const { Option } = Select;
const { TextArea } = Input;

const DeductionModal = ({ visible, onCancel, onSuccess, employeeId, editingRecord, dateOfJoining }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [deductionTypes, setDeductionTypes] = useState([]);
    const [typesLoading, setTypesLoading] = useState(false);

    // Disable dates before joining date
    const disabledDate = (current) => {
        if (!dateOfJoining) return false;
        return current && current < dayjs(dateOfJoining).startOf('day');
    };

    // Fetch deduction types from API
    useEffect(() => {
        const fetchTypes = async () => {
            setTypesLoading(true);
            const response = await adminactions.fetchDeductionTypes();
            if (response) {
                // Handle both array and paginated response formats
                if (Array.isArray(response)) {
                    setDeductionTypes(response);
                } else if (response.results) {
                    setDeductionTypes(response.results);
                }
            }
            setTypesLoading(false);
        };
        fetchTypes();
    }, []);

    useEffect(() => {
        if (visible) {
            if (editingRecord && deductionTypes.length > 0) {
                form.setFieldsValue({
                    ...editingRecord,
                    deduction_type_fk_id: editingRecord.deduction_type_fk?.id || editingRecord.deduction_type_fk_id || editingRecord.deduction_type,
                    effective_date: editingRecord.effective_date ? dayjs(editingRecord.effective_date) : null,
                    end_date: editingRecord.end_date ? dayjs(editingRecord.end_date) : null
                });
            } else if (!editingRecord) {
                form.resetFields();
            }
        }
    }, [visible, editingRecord, form, deductionTypes]);

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const data = {
                employee: employeeId,
                effective_date: values.effective_date.format('YYYY-MM-DD'),
                deduction_type_fk_id: values.deduction_type_fk_id,
                deduction_name: values.deduction_name || null,
                amount: values.amount,
                end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : null,
                is_attendance_based: values.is_attendance_based || false,
                notes: values.notes || null
            };

            if (editingRecord) {
                await adminactions.updateDeduction(editingRecord.id, data);
                message.success('Deduction updated successfully');
            } else {
                await adminactions.createDeduction(data);
                message.success('Deduction created successfully');
            }

            onSuccess();
            form.resetFields();
        } catch (error) {
            message.error('Failed to save deduction');
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={editingRecord ? 'Edit Deduction' : 'Add Deduction'}
            open={visible}
            onCancel={onCancel}
            onOk={() => form.submit()}
            confirmLoading={loading}
            width={700}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    deduction_type: 'Other'
                }}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="effective_date"
                            label="Effective Date"
                            rules={[{ required: true, message: 'Please select effective date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} disabledDate={disabledDate} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="deduction_type_fk_id"
                            label="Deduction Type"
                            rules={[{ required: true, message: 'Please select deduction type' }]}
                        >
                            <Select placeholder="Select type" loading={typesLoading}>
                                {deductionTypes.map(type => (
                                    <Option key={type.id} value={type.id}>
                                        {type.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="deduction_name"
                            label="Custom Name (Optional)"
                        >
                            <Input placeholder="Enter custom deduction name" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="amount"
                            label="Amount"
                            rules={[{ required: true, message: 'Please enter amount' }]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                step={100}
                                prefix="₹"
                                placeholder="Enter deduction amount"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="is_attendance_based"
                            label="Attendance Based"
                            valuePropName="checked"
                            tooltip="If enabled, this deduction will be prorated based on days present (including joining date)"
                        >
                            <Switch />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="end_date"
                            label="End Date (Optional)"
                            dependencies={['effective_date']}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        const startDate = getFieldValue('effective_date');
                                        if (!value || !startDate) {
                                            return Promise.resolve();
                                        }
                                        if (value.isBefore(startDate) || value.isSame(startDate, 'day')) {
                                            return Promise.reject(new Error('Start Date must be earlier than End Date'));
                                        }
                                        return Promise.resolve();
                                    },
                                }),
                            ]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="notes"
                            label="Notes"
                        >
                            <TextArea rows={3} placeholder="Enter any notes" />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default DeductionModal;
